import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BillingPanel } from "../components/billing/BillingPanel";
import { ProfileTab } from "../components/settings/ProfileTab";
import { AccountTab } from "../components/settings/AccountTab";
import { BazaarTab } from "../components/settings/BazaarTab";
import { SettingsSearch } from "../components/settings/SettingsSearch";
import { ArrowLeft, User, KeyRound, Store, Crown, LogOut } from "lucide-react";

const TABS = [
  { id: "profile", label: "Profile", icon: User },
  { id: "account", label: "Account", icon: KeyRound },
  { id: "bazaar", label: "Bazaar", icon: Store },
  { id: "subscription", label: "Subscription", icon: Crown },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "profile";

  const setTab = (id) => setSearchParams({ tab: id });

  const jumpToSetting = (targetTab, sectionId) => {
    setTab(targetTab);
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("settings-highlight");
      setTimeout(() => el.classList.remove("settings-highlight"), 1600);
    }, 150);
  };

  return (
    <div className="min-h-screen bg-[#151613] text-[#f1e0c5]" data-testid="settings-page">
      <nav className="flex items-center px-6 md:px-12 py-6 border-b border-white/5">
        <button data-testid="settings-back-button" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-[#f1e0c5]/60 hover:text-[#D4AF37]">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>
      </nav>

      <div className="px-6 md:px-12 pt-8 max-w-5xl mx-auto">
        <SettingsSearch onJump={jumpToSetting} />
      </div>

      <div className="px-6 md:px-12 py-8 max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8">
        <div className="flex md:flex-col gap-1.5 overflow-x-auto md:overflow-visible" data-testid="settings-tab-list">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`settings-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-full md:rounded-lg text-sm whitespace-nowrap transition-colors ${
                tab === id ? "bg-[#D4AF37] text-black font-medium" : "text-[#f1e0c5]/60 hover:bg-white/5 hover:text-[#f1e0c5]"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
          <button
            data-testid="settings-logout-button"
            onClick={logout}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-full md:rounded-lg text-sm whitespace-nowrap text-[#D90429]/80 hover:bg-[#D90429]/10 hover:text-[#D90429] md:mt-6 md:pt-4 md:border-t md:border-white/5"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        <div className="min-w-0">
          {tab === "profile" && <ProfileTab />}
          {tab === "account" && <AccountTab />}
          {tab === "bazaar" && <BazaarTab />}
          {tab === "subscription" && <div id="settings-section-billing"><BillingPanel /></div>}
        </div>
      </div>
    </div>
  );
}
